import { useState, useEffect, useCallback, useRef } from "react";
import api from "../../services/api";
import ProtectedLink from "../../components/ProtectedLink";

const EMPTY = {
  sku: "", ean: "", name: "", brand: "", category: "Général", unit: "pièce",
  description: "", purchase_price: "", selling_price: "", stock_min: "", stock_reorder: "",
  default_supplier_id: "", expiry_date: "", production_date: "", origin_country: "",
  hs_code: "", weight_kg: "", dimensions: "", product_type: "produit", name_en: "",
};
const UNITS = ["pièce", "kg", "g", "L", "mL", "m", "cm", "boîte", "carton", "lot", "heure"];
const CARRIERS = ["DHL", "Colissimo", "DPD", "FedEx", "Manuel"];

const COUNTRIES = [
  { code: "FR", name: "France" }, { code: "DE", name: "Allemagne" }, { code: "ES", name: "Espagne" },
  { code: "IT", name: "Italie" }, { code: "CN", name: "Chine" }, { code: "US", name: "États-Unis" },
  { code: "GB", name: "Royaume-Uni" }, { code: "JP", name: "Japon" }, { code: "IN", name: "Inde" },
  { code: "BR", name: "Brésil" }, { code: "CA", name: "Canada" }, { code: "AU", name: "Australie" },
  { code: "MX", name: "Mexique" }, { code: "KR", name: "Corée du Sud" }, { code: "TW", name: "Taïwan" },
  { code: "VN", name: "Vietnam" }, { code: "TH", name: "Thaïlande" }, { code: "MA", name: "Maroc" },
  { code: "TN", name: "Tunisie" }, { code: "TR", name: "Turquie" }, { code: "PL", name: "Pologne" },
  { code: "PT", name: "Portugal" }, { code: "BE", name: "Belgique" }, { code: "CH", name: "Suisse" },
  { code: "NL", name: "Pays-Bas" }, { code: "SE", name: "Suède" }, { code: "AT", name: "Autriche" },
  { code: "CZ", name: "Tchéquie" }, { code: "RO", name: "Roumanie" }, { code: "HU", name: "Hongrie" },
];

function countryFlag(code) {
  if (!code || code.length !== 2) return "🏳";
  return [...code.toUpperCase()].map(c => String.fromCodePoint(0x1F1E6 + c.charCodeAt(0) - 65)).join("");
}

function marginBadge(purchase, selling) {
  const pp = parseFloat(purchase) || 0;
  const sp = parseFloat(selling) || 0;
  if (sp <= 0) return null;
  const rate = ((sp - pp) / sp) * 100;
  const color = rate < 20 ? "#dc2626" : rate < 40 ? "#d97706" : "#16a34a";
  const bg = rate < 20 ? "#fef2f2" : rate < 40 ? "#fffbeb" : "#f0fdf4";
  return (
    <span style={{ background: bg, color, border: `1px solid ${color}33`, borderRadius: 6, padding: "1px 7px", fontSize: 11, fontWeight: 700 }}>
      {rate.toFixed(1)}%
    </span>
  );
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const diff = Math.floor((new Date(dateStr) - new Date()) / 86400000);
  return diff;
}

function kgToDisplay(kg, unit) {
  if (kg === '' || kg == null) return '';
  const v = parseFloat(kg);
  if (isNaN(v)) return '';
  if (unit === 'g') return String(Math.round(v * 1000 * 1000) / 1000);
  return String(v);
}
function displayToKg(value, unit) {
  if (value === '' || value == null) return null;
  const v = parseFloat(value);
  if (isNaN(v)) return null;
  if (unit === 'g') return v / 1000;
  return v;
}
function weightStep(unit) {
  return unit === 'g' ? '1' : '0.001';
}
function weightPlaceholder(unit) {
  return unit === 'g' ? '500' : '0.500';
}

export default function CatalogTab({ currentCompanyId, suppliers = [], companies = [], onRefresh, openOnMount = false }) {
  const currentCompany = companies.find(c => String(c.id) === String(currentCompanyId));
  const weightUnit = currentCompany?.weight_unit_preference || 'kg';
  const [products, setProducts]       = useState([]);
  const [total, setTotal]             = useState(0);
  const [categories, setCategories]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState("");
  const [category, setCategory]       = useState("");
  const [lowStock, setLowStock]       = useState(false);
  const [typeFilter, setTypeFilter]   = useState("");
  const [page, setPage]               = useState(1);
  const [totalPages, setTotalPages]   = useState(1);
  const PAGE_SIZE = 50;
  const [showModal, setShowModal]     = useState(false);
  const [editTarget, setEditTarget]   = useState(null);
  const [form, setForm]               = useState(EMPTY);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState("");
  const [showImport, setShowImport]   = useState(false);
  const [importText, setImportText]   = useState("");
  const [importPreview, setImportPreview] = useState([]);
  const [importResult, setImportResult]   = useState(null);
  const [deleteTarget, setDeleteTarget]   = useState(null);
  const [imgUploading, setImgUploading]   = useState(false);
  const [dragOver, setDragOver]           = useState(false);
  const [previewImg, setPreviewImg]       = useState(null);
 useEffect(() => {
  if (openOnMount) {
    const timer = setTimeout(() => {
      setEditTarget(null);
      setForm(EMPTY);
      setPreviewImg(null);
      setError("");
      setShowModal(true);
    }, 100);
    return () => clearTimeout(timer);
  }
}, [openOnMount]);
  const eanRef  = useRef(null);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    if (!currentCompanyId) return;
    setLoading(true);
    api.stocks.products(currentCompanyId, { search, category, low_stock: lowStock ? "1" : "", product_type: typeFilter, page, limit: PAGE_SIZE })
      .then(r => {
        setProducts(r.products || []); setTotal(r.total || 0); setCategories(r.categories || []);
        setTotalPages(r.totalPages || 1);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [currentCompanyId, search, category, lowStock, typeFilter, page]);

  useEffect(() => { load(); }, [load]);

  // Revenir à la page 1 uniquement quand un FILTRE change (pas à chaque rechargement)
  useEffect(() => { setPage(1); }, [search, category, lowStock, typeFilter]);

  const resetFilters = () => { setSearch(""); setCategory(""); setLowStock(false); setTypeFilter(""); setPage(1); };

  const openCreate = () => { setEditTarget(null); setForm(EMPTY); setPreviewImg(null); setError(""); setShowModal(true); };
  const openEdit = (p) => {
    setEditTarget(p);
    setForm({
      sku: p.sku, ean: p.ean || "", name: p.name, brand: p.brand || "",
      category: p.category, unit: p.unit, description: p.description || "",
      purchase_price: String(p.purchase_price), selling_price: String(p.selling_price),
      stock_min: String(p.stock_min), stock_reorder: String(p.stock_reorder),
      default_supplier_id: String(p.default_supplier_id || ""),
      expiry_date: p.expiry_date || "", production_date: p.production_date || "",
      origin_country: p.origin_country || "", hs_code: p.hs_code || "",
      weight_kg: kgToDisplay(p.weight_kg, weightUnit),
      dimensions: p.dimensions || "",
      product_type: p.product_type || "produit",
      name_en: p.name_en || "",
    });
    setPreviewImg(p.image_url ? api.stocks.productImgUrl(p.image_url) : null);
    setError(""); setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(""); setSaving(true);
    try {
      const payload = {
        ...form, company_id: currentCompanyId,
        purchase_price: parseFloat(form.purchase_price) || 0,
        selling_price: parseFloat(form.selling_price) || 0,
        stock_min: parseFloat(form.stock_min) || 0,
        stock_reorder: parseFloat(form.stock_reorder) || 0,
        weight_kg: displayToKg(form.weight_kg, weightUnit),
        default_supplier_id: form.default_supplier_id ? form.default_supplier_id : null,
      };
      if (editTarget) await api.stocks.updateProduct(editTarget.id, payload);
      else await api.stocks.createProduct(payload);
      setShowModal(false); load(); onRefresh?.();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await api.stocks.deleteProduct(deleteTarget.id, currentCompanyId).catch(console.error);
    setDeleteTarget(null); load(); onRefresh?.();
  };
  const [permanentError, setPermanentError] = useState("");
  const handlePermanentDelete = async () => {
    if (!deleteTarget) return;
    setPermanentError("");
    try {
      await api.stocks.deleteProductPermanent(deleteTarget.id);
      setDeleteTarget(null); load(); onRefresh?.();
    } catch (e) {
      setPermanentError(e.message);
    }
  };

  const uploadImage = async (file) => {
    if (!editTarget) return;
    if (!file.type.startsWith("image/")) { setError("Fichier non supporté (image uniquement)"); return; }
    setImgUploading(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const r = await api.stocks.uploadProductImage(editTarget.id, { image_data: ev.target.result });
        setPreviewImg(api.stocks.productImgUrl(r.url));
        load();
      } catch (e) { setError(e.message); }
      finally { setImgUploading(false); }
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) uploadImage(file);
  };

  const parseCSV = (text) => {
    const lines = text.trim().split("\n").filter(l => l.trim());
    if (lines.length < 2) return [];
    const header = lines[0].split(";").map(h => h.trim().toLowerCase());
    return lines.slice(1).map(line => {
      const cols = line.split(";");
      const row = {};
      header.forEach((h, i) => { row[h] = cols[i]?.trim() || ""; });
      return {
        sku: row["sku"] || row["référence"] || "", ean: row["ean"] || "",
        name: row["nom"] || row["name"] || "", brand: row["marque"] || row["brand"] || "",
        category: row["catégorie"] || row["category"] || "Général", unit: row["unité"] || row["unit"] || "pièce",
        purchase_price: row["pa ht"] || row["purchase_price"] || "0",
        selling_price: row["pv ht"] || row["selling_price"] || "0",
        stock_min: row["seuil alerte"] || row["stock_min"] || "0",
      };
    }).filter(r => r.name);
  };

  const handleEanSearch = async (ean) => {
    if (!ean) return;
    try { const p = await api.stocks.productByEan(ean, currentCompanyId); openEdit(p); }
    catch { setError(`EAN "${ean}" non trouvé.`); setTimeout(() => setError(""), 3000); }
    if (eanRef.current) eanRef.current.value = "";
  };

  const purchaseP = parseFloat(form.purchase_price);
  const sellingP  = parseFloat(form.selling_price);
  const marginPct = (!isNaN(purchaseP) && !isNaN(sellingP) && purchaseP > 0)
    ? ((sellingP - purchaseP) / purchaseP) * 100
    : null;

  return (
    <div>
      {/* Barre de recherche */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <input className="form-control" placeholder="🔍 Rechercher nom, SKU, EAN, marque…" style={{ flex: 1, minWidth: 200 }}
          value={search} onChange={e => setSearch(e.target.value)} />
        <select className="form-control" style={{ width: 150 }} value={category} onChange={e => setCategory(e.target.value)}>
          <option value="">Toutes catégories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}>
          <input type="checkbox" checked={lowStock} onChange={e => setLowStock(e.target.checked)} />
          Stock bas seulement
        </label>
        <select className="form-control" style={{ maxWidth: 160 }} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="">Tous types</option>
          <option value="produit">📦 Produits</option>
          <option value="service">🛠️ Services</option>
        </select>
        <button className="btn" onClick={resetFilters} title="Effacer la recherche, la catégorie et revenir à la page 1">🔄 Réinitialiser</button>
        <button className="btn" onClick={() => setShowImport(true)}>📤 Importer CSV</button>
        <ProtectedLink url={api.stocks.exportProducts(currentCompanyId)} style={{ textDecoration: "none" }}>
          <button className="btn">⬇️ Export CSV</button>
        </ProtectedLink>
        <button className="btn primary" onClick={openCreate}>+ Nouveau produit</button>
      </div>

      {/* Scanner EAN */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center" }}>
        <span style={{ fontSize: 13, color: "var(--text-muted)", whiteSpace: "nowrap" }}>📷 Scanner EAN :</span>
        <input ref={eanRef} className="form-control" placeholder="Scanner ou saisir un code EAN-13…" style={{ maxWidth: 280 }}
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleEanSearch(e.target.value.trim()); } }} />
      </div>

      {error && <div className="alert danger" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{total} produit(s) · {products.length} affichés</div>
          {totalPages > 1 && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
              <button className="btn sm" disabled={page <= 1} onClick={() => setPage(p => Math.max(p - 1, 1))}>← Précédent</button>
              <span style={{ color: "var(--text-muted)" }}>Page {page} / {totalPages}</span>
              <button className="btn sm" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(p + 1, totalPages))}>Suivant →</button>
            </div>
          )}
        </div>
        <div className="tbl-overflow">
          <table className="tbl">
            <thead>
              <tr>
                <th>Photo</th><th>SKU</th><th>Produit</th><th>Origine</th>
                <th>Stock</th><th>PA HT</th><th>PV HT</th><th>Marge</th>
                <th>Expiration</th><th>Statut</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={11} style={{ textAlign: "center", padding: 30, color: "var(--text-muted)" }}>Chargement…</td></tr>
              ) : products.length === 0 ? (
                <tr><td colSpan={11} style={{ textAlign: "center", padding: 30, color: "var(--text-muted)" }}>Aucun produit. Créez-en un ou importez un fichier CSV.</td></tr>
              ) : products.map(p => {
                // Disponible = physique − réservé (picking en cours, cf.
                // utils/stockAvailability.js) — c'est cette valeur qui doit
                // piloter l'alerte "rien à vendre", pas le stock physique
                // brut qui peut déjà être entièrement pointé pour un autre
                // client sans avoir encore quitté le rayon.
                const available = p.available_stock ?? p.stock_current;
                const isAlert = available <= p.stock_min && p.stock_min > 0;
                const isEmpty = available <= 0;
                const days = daysUntil(p.expiry_date);
                const expiryAlert = days !== null && days < 90;
                const imgUrl = api.stocks.productImgUrl(p.image_url);
                return (
                  <tr key={p.id}>
                    <td style={{ width: 48 }}>
                      {imgUrl
                        ? <img src={imgUrl} alt="" style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 6, border: "1px solid var(--border-color)" }} />
                        : <div style={{ width: 40, height: 40, background: "#f3f4f6", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>📦</div>
                      }
                    </td>
                    <td style={{ fontFamily: "monospace", fontSize: 12, color: "var(--g)" }}>{p.sku}</td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{p.product_type === "service" ? "🛠️ " : "📦 "}{p.name}</div>
                      {p.brand && <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{p.brand}</div>}
                      {p.hs_code && <div style={{ fontSize: 10, color: "var(--text-muted)" }}>SH: {p.hs_code}</div>}
                    </td>
                    <td style={{ fontSize: 16 }}>
                      {p.origin_country ? (
                        <span title={COUNTRIES.find(c => c.code === p.origin_country)?.name || p.origin_country}>
                          {countryFlag(p.origin_country)}
                        </span>
                      ) : "—"}
                    </td>
                    <td style={{ fontWeight: 700, color: isEmpty ? "var(--red)" : isAlert ? "var(--amber)" : "inherit" }}>
                      {p.product_type === "service" ? "—" : (
                        <>
                          {available} {p.unit}
                          {p.reserved_stock > 0 && (
                            <div style={{ fontSize: 10, color: "var(--text-muted)", fontWeight: 400 }}>
                              {p.stock_current} en rayon, {p.reserved_stock} réservé
                            </div>
                          )}
                          {isAlert && <div style={{ fontSize: 10, color: "var(--amber)" }}>min {p.stock_min}</div>}
                        </>
                      )}
                    </td>
                    <td style={{ color: "var(--text-muted)" }}>{(p.purchase_price || 0).toFixed(2)} €</td>
                    <td style={{ fontWeight: 600 }}>{(p.selling_price || 0).toFixed(2)} €</td>
                    <td>{marginBadge(p.purchase_price, p.selling_price)}</td>
                    <td>
                      {p.expiry_date ? (
                        <span style={{ color: expiryAlert ? "#dc2626" : "inherit", fontWeight: expiryAlert ? 700 : 400, fontSize: 12 }}>
                          {new Date(p.expiry_date).toLocaleDateString("fr-FR")}
                          {expiryAlert && <div style={{ fontSize: 10 }}>⚠️ {days < 0 ? "Expiré" : `${days}j`}</div>}
                        </span>
                      ) : "—"}
                    </td>
                    <td>
                      {p.product_type === "service" ? <span className="badge">Service</span>
                        : isEmpty ? <span className="badge danger">Rupture</span>
                        : isAlert ? <span className="badge warning">Stock bas</span>
                        : <span className="badge success">OK</span>}
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button className="btn sm" onClick={() => openEdit(p)} title="Modifier">✏️</button>
                        <button className="btn sm" style={{ color: "var(--red)" }} onClick={() => setDeleteTarget(p)} title="Archiver">🗑</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Créer / Modifier */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal" style={{ width: 920, maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto" }}>
            <div className="modal-header">
              <h3 className="modal-title">{editTarget ? `Modifier — ${editTarget.name}` : "Nouveau produit"}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}
            <form onSubmit={handleSubmit}>
              <div style={{ display: "grid", gridTemplateColumns: "280px 1fr", gap: 24 }}>

                {/* ── Colonne gauche : visuels + description + douanes ── */}
                <div>
                  {editTarget && (
                    <>
                      <label className="form-label">Photo principale</label>
                      <div
                        onClick={() => fileRef.current?.click()}
                        style={{ border: "2px dashed var(--border-strong)", borderRadius: 10, height: 220, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, background: "var(--surface-1)", marginBottom: 8, cursor: "pointer", overflow: "hidden" }}
                      >
                        {previewImg ? (
                          <img src={previewImg} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        ) : (
                          <>
                            <span style={{ fontSize: 28 }}>📷</span>
                            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Glisser-déposer ou cliquer</span>
                          </>
                        )}
                      </div>
                      <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={e => { const f = e.target.files[0]; if (f) uploadImage(f); }} />

                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
                        <div title="Bientôt disponible" style={{ border: "1px dashed var(--border)", borderRadius: 8, height: 56, display: "flex", alignItems: "center", justifyContent: "center", background: "var(--surface-1)", opacity: 0.5, cursor: "not-allowed" }}>
                          <span style={{ fontSize: 15, color: "var(--text-muted)" }}>+</span>
                        </div>
                        <div title="Bientôt disponible" style={{ border: "1px dashed var(--border)", borderRadius: 8, height: 56, display: "flex", alignItems: "center", justifyContent: "center", background: "var(--surface-1)", opacity: 0.5, cursor: "not-allowed" }}>
                          <span style={{ fontSize: 15, color: "var(--text-muted)" }}>+</span>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="form-group">
                    <label className="form-label">Description</label>
                    <input className="form-control" placeholder="Description courte…" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
                  </div>

                  <div style={{ fontSize: 12, fontWeight: 500, color: "var(--text-secondary)", margin: "12px 0 8px", textTransform: "uppercase", letterSpacing: "0.03em", borderTop: "0.5px solid var(--border)", paddingTop: 12 }}>Douanes</div>

                  <div className="form-group">
                    <label className="form-label">Pays d'origine</label>
                    <input className="form-control" placeholder="France" value={form.origin_country} onChange={e => setForm(f => ({ ...f, origin_country: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Code SH (douanier)</label>
                    <input className="form-control" placeholder="6109.10" value={form.hs_code} onChange={e => setForm(f => ({ ...f, hs_code: e.target.value }))} />
                  </div>
                </div>

                {/* ── Colonne droite : identité + prix + stock ── */}
                <div>
                  <div className="form-group" style={{ marginBottom: 14 }}>
                    <label className="form-label">Type</label>
                    <div className="btn-group">
                      <button type="button" className={`btn sm${form.product_type === "produit" ? " primary" : ""}`} onClick={() => setForm(f => ({ ...f, product_type: "produit" }))}>📦 Produit</button>
                      <button type="button" className={`btn sm${form.product_type === "service" ? " primary" : ""}`} onClick={() => setForm(f => ({ ...f, product_type: "service" }))}>🛠️ Service</button>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">SKU {!editTarget && "(auto si vide)"}</label>
                      <input className="form-control" placeholder="SKU-001" value={form.sku} onChange={e => setForm(f => ({ ...f, sku: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">EAN-13</label>
                      <input className="form-control" placeholder="3760xxxxxxxxx" value={form.ean} onChange={e => setForm(f => ({ ...f, ean: e.target.value }))} />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Prix achat HT (€) *</label>
                      <input className="form-control" type="number" step="0.01" required value={form.purchase_price} onChange={e => setForm(f => ({ ...f, purchase_price: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Prix vente HT (€)</label>
                      <input className="form-control" type="number" step="0.01" value={form.selling_price} onChange={e => setForm(f => ({ ...f, selling_price: e.target.value }))} />
                    </div>
                  </div>

                  {marginPct !== null && (
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, background: "var(--bg-success)", borderRadius: "var(--radius)", padding: "6px 12px" }}>
                      <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>Marge</span>
                      <span style={{ fontSize: 13, fontWeight: 500, color: "var(--text-success)" }}>
                        {(parseFloat(form.selling_price || 0) - parseFloat(form.purchase_price || 0)).toFixed(2)} €
                      </span>
                      <span style={{ fontSize: 12, color: "var(--text-success)" }}>({marginPct.toFixed(1)} %)</span>
                    </div>
                  )}

                  <div className="form-group">
                    <label className="form-label">Nom du produit *</label>
                    <input className="form-control" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">🇬🇧 Nom (anglais)</label>
                    <input className="form-control" placeholder="Traduction manuelle, optionnelle" value={form.name_en} onChange={e => setForm(f => ({ ...f, name_en: e.target.value }))} />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Marque</label>
                      <input className="form-control" value={form.brand} onChange={e => setForm(f => ({ ...f, brand: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Catégorie</label>
                      <input className="form-control" list="cat-list" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} />
                      <datalist id="cat-list">{categories.map(c => <option key={c} value={c} />)}</datalist>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Fournisseur</label>
                    <select className="form-control" value={form.default_supplier_id} onChange={e => setForm(f => ({ ...f, default_supplier_id: e.target.value }))}>
                      <option value="">— Aucun —</option>
                      {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Date d'expiration</label>
                      <input className="form-control" type="date" value={form.expiry_date} onChange={e => setForm(f => ({ ...f, expiry_date: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Date de production</label>
                      <input className="form-control" type="date" value={form.production_date} onChange={e => setForm(f => ({ ...f, production_date: e.target.value }))} />
                    </div>
                  </div>

                  {form.product_type !== "service" && (
                    <>
                      {editTarget && (
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                          <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>Unités disponibles</span>
                          <div style={{ background: "var(--surface-1)", border: "0.5px solid var(--border)", borderRadius: "var(--radius)", padding: "4px 14px", display: "flex", alignItems: "baseline", gap: 5 }}>
                            <span style={{ fontSize: 16, fontWeight: 500 }}>{editTarget.available_stock ?? editTarget.stock_current}</span>
                            <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{form.unit}</span>
                            {editTarget.reserved_stock > 0 && (
                              <span style={{ fontSize: 10, color: "var(--text-muted)" }}>({editTarget.stock_current} en rayon, {editTarget.reserved_stock} réservé)</span>
                            )}
                          </div>
                        </div>
                      )}
                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label">Unité</label>
                          <select className="form-control" value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}>
                            <option value="pièce">pièce</option>
                            <option value="kg">kg</option>
                            <option value="L">L</option>
                            <option value="m">m</option>
                            <option value="lot">lot</option>
                            <option value="boîte">boîte</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Poids ({weightUnit})</label>
                          <input className="form-control" type="number" step={weightStep(weightUnit)} placeholder={weightPlaceholder(weightUnit)} value={form.weight_kg} onChange={e => setForm(f => ({ ...f, weight_kg: e.target.value }))} />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label">Seuil d'alerte</label>
                          <input className="form-control" type="number" step="1" value={form.stock_min} onChange={e => setForm(f => ({ ...f, stock_min: e.target.value }))} />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Seuil recommandé</label>
                          <input className="form-control" type="number" step="1" value={form.stock_reorder} onChange={e => setForm(f => ({ ...f, stock_reorder: e.target.value }))} />
                        </div>
                      </div>
                    </>
                  )}

                  <div className="btn-group" style={{ justifyContent: "flex-end", marginTop: 16 }}>
                    <button type="button" className="btn" onClick={() => setShowModal(false)}>Annuler</button>
                    <button type="submit" className="btn primary" disabled={saving}>{saving ? "Enregistrement…" : editTarget ? "Modifier" : "Créer"}</button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Import CSV */}
      {showImport && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowImport(false)}>
          <div className="modal" style={{ maxWidth: 700 }}>
            <div className="modal-header">
              <h3 className="modal-title">📤 Import produits CSV</h3>
              <button className="modal-close" onClick={() => setShowImport(false)}>✕</button>
            </div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12, background: "#f9fafb", padding: 10, borderRadius: 8 }}>
              Format attendu (séparateur ;) :<br />
              <code>SKU;EAN;Nom;Marque;Catégorie;Unité;PA HT;PV HT;Seuil alerte</code>
            </div>
            <textarea className="form-control" rows={8} placeholder="Collez votre CSV ici…" value={importText}
              onChange={e => { setImportText(e.target.value); setImportPreview([]); setImportResult(null); }}
              style={{ fontFamily: "monospace", fontSize: 12 }} />
            {importResult && <div className="alert success" style={{ marginTop: 10 }}>Importés : {importResult.created} · Ignorés : {importResult.skipped}</div>}
            {importPreview.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>Aperçu ({importPreview.length} ligne(s)) :</div>
                <div className="tbl-overflow" style={{ maxHeight: 200 }}>
                  <table className="tbl" style={{ fontSize: 11 }}>
                    <thead><tr><th>SKU</th><th>EAN</th><th>Nom</th><th>Catégorie</th><th>PA HT</th></tr></thead>
                    <tbody>{importPreview.slice(0, 10).map((r, i) => <tr key={i}><td>{r.sku || "auto"}</td><td>{r.ean}</td><td>{r.name}</td><td>{r.category}</td><td>{r.purchase_price} €</td></tr>)}</tbody>
                  </table>
                </div>
              </div>
            )}
            <div className="btn-group" style={{ justifyContent: "flex-end", marginTop: 12 }}>
              <button className="btn" onClick={() => setShowImport(false)}>Fermer</button>
              <button className="btn" onClick={() => { setImportPreview(parseCSV(importText)); setImportResult(null); }} disabled={!importText}>Aperçu</button>
              <button className="btn primary" onClick={async () => {
                if (!importPreview.length) return;
                setSaving(true);
                try { const r = await api.stocks.importProducts({ products: importPreview, company_id: currentCompanyId }); setImportResult(r); load(); onRefresh?.(); }
                catch (e) { setError(e.message); }
                finally { setSaving(false); }
              }} disabled={!importPreview.length || saving}>
                {saving ? "Import en cours…" : `Importer ${importPreview.length} produit(s)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal suppression */}
      {deleteTarget && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setDeleteTarget(null)}>
          <div className="modal" style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <h3 className="modal-title">Archiver le produit</h3>
              <button className="modal-close" onClick={() => setDeleteTarget(null)}>✕</button>
            </div>
            <p style={{ color: "var(--text-muted)", marginBottom: 16 }}>
              Archiver <strong>{deleteTarget.name}</strong> ? Il n'apparaîtra plus dans le catalogue mais ses mouvements seront conservés.
            </p>
            {permanentError && <div className="alert alert-error" style={{ marginBottom: 12, fontSize: 13 }}>{permanentError}</div>}
            <div className="btn-group" style={{ justifyContent: "space-between" }}>
              <button className="btn sm" style={{ color: "var(--red)" }} onClick={handlePermanentDelete} title="Suppression irréversible, bloquée si historique de vente">
                Supprimer définitivement
              </button>
              <div className="btn-group">
                <button className="btn" onClick={() => setDeleteTarget(null)}>Annuler</button>
                <button className="btn primary" style={{ background: "var(--red)" }} onClick={handleDelete}>Archiver</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
